import { Icon as ChakraIcon, HTMLChakraProps, ChakraProps } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import * as icons from './svgs';

import { getSvgFill } from '_helpers';
import { forwardRef } from 'system';

export const iconsList = Object.keys(icons);

const propTypes = {
  type: PropTypes.string.isRequired,
  boxSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  // ? NOTE: Even though we might hate string booleans it is technically correct in lower level chakra implementations. (i.e real world element attributes)
  focusable: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['true', 'false'])]),
  role: PropTypes.string
};

const defaultProps = {
  color: 'currentColor',
  boxSize: '1em',
  focusable: false,
  role: undefined
};

type ExternalComponentProps = HTMLChakraProps<'svg'>;
type InternalChakraIconProps = Omit<React.SVGAttributes<SVGElement>, keyof ChakraProps>;

export type IconProps = ExternalComponentProps & { type: string };

const Icon = forwardRef<IconProps, 'svg'>((props: IconProps & typeof defaultProps, ref) => {
  const { type, color, ...rest } = props;

  const Component = icons[type];

  const mergedProps = { color, ...rest } as InternalChakraIconProps;

  useEffect(() => {
    if (!icons[type]) {
      // eslint-disable-next-line no-console
      console.error(`Invalid type passed to <Icon />. Unable to find an icon with the name "${type}"`);
    }
  }, [type]);

  return (
    <ChakraIcon {...mergedProps} ref={ref} viewBox="0 0 24 24">
      {icons[type] ? <Component data-testid="icon-svg" fill={getSvgFill(color)} /> : <></>}
    </ChakraIcon>
  );
});

Icon.displayName = 'Icon';
Icon.propTypes = propTypes;
Icon.defaultProps = defaultProps;

export default Icon;
